import { useEffect, useState, useMemo } from "react";
import {
  AcademyIcon,
  BellIcon,
  ChatIcon,
  GlobalIcon,
  MarketplaceIcon,
  TripIcon,
  SocialIcon,
  LearnIcon,
  WorkIcon,
  DashboardIcon,
  CardIcon,
  DocumentIcon,
  ResumeIcon,
  AirplaneIcon,
  MapIcon,
  FlagIcon,
  AccommodationIcon,
  TourIcon,
  UmbrellaIcon,
  SourceIcon,
  VisaIcon,
  ConservationIcon,
  DealIcon,
  HostFamilyIcon,
  UpgradeIcon,
} from "@/components/Icon";
import { Division, Trip } from "@/fetch/gworld";
import { SocialMobileHeader, SocialSidebar } from "@/components/Social";
import { JobsMobileHeader } from "@/components/Jobs";
import { AcademyMobileHeader } from "@/components/Academy";
import { Route, RouteFeature, SubrouteFeature } from "./useRoutes";
import useDivisionMenuOrder from "./useDivisonMenuOrder";
import useResponsive from "./useResponsive";

const WORKING_HOLIDAY_ARRIVAL_ONLY_CODES = ["WH-AA", "WH-UU", "WH-CC"];

const getRoutes = ({
  trip,
  hasSocialGroup,
  isPaymentLockEnabled,
  isTripCompleted,
  isAfricanRegion,
  getOrder,
  isMobile,
}: {
  trip: Trip | undefined | null;
  hasSocialGroup: boolean;
  isPaymentLockEnabled: boolean;
  isTripCompleted: boolean;
  isAfricanRegion: boolean;
  getOrder: (routeId: string, subRouteId?: string) => undefined | number;
  isMobile: boolean;
}) => {
  const isWHArrivalOnly = Boolean(
    WORKING_HOLIDAY_ARRIVAL_ONLY_CODES.find((each) => each === trip?.trip_code)
  );
  const routes: Route[] = [
    {
      name: isMobile ? "Trip" : "Experience",
      path: "/experience/dashboard",
      features: [
        RouteFeature.Sidebar,
        RouteFeature.MobileTab,
        RouteFeature.Tab,
        RouteFeature.HasMobileTripSelector,
      ],
      icon: TripIcon,
      id: "1",
      order: getOrder("1") || 1,
      children: [
        {
          name: "Dashboard",
          path: "/experience/dashboard",
          groupIndex: 0,
          icon: DashboardIcon,
          disabled: isPaymentLockEnabled,
          id: "1-1",
          order: getOrder("1", "1-1") || 1,
        },
        {
          name: "Payments",
          path: "/experience/payments",
          icon: CardIcon,
          groupIndex: 0,
          features: [SubrouteFeature.HideSuggestionBanner],
          id: "1-2",
          order: getOrder("1", "1-2") || 2,
        },
        {
          name: "Documents",
          path: "/experience/documents",
          icon: DocumentIcon,
          groupIndex: 0,
          disabled: isPaymentLockEnabled,
          isAvailable: trip?.division !== Division.Superlite,
          id: "1-3",
          order: getOrder("1", "1-3") || 3,
        },
        {
          name: "Resume",
          path: "/experience/resume",
          icon: ResumeIcon,
          groupIndex: 0,
          disabled: isPaymentLockEnabled,
          isAvailable:
            trip?.division !== Division.Remote &&
            trip?.division !== Division.Tour &&
            trip?.division !== Division.Getaways,
          id: "1-4",
          order: getOrder("1", "1-4") || 4,
        },
        {
          name: `${
            trip?.division === Division.AuPair
              ? "Au Pair"
              : trip?.division === Division.Tutor
              ? "Tutor"
              : ""
          } Profile`,
          icon: ResumeIcon,
          path: "/experience/au-pair-profile",
          groupIndex: 0,
          disabled: isPaymentLockEnabled,
          isAvailable:
            trip?.division === Division.AuPair ||
            trip?.division === Division.Tutor,
          id: "1-5",
          order: getOrder("1", "1-5") || 5,
        },
        {
          name: "Trip Details",
          path: "/experience/trip-details",
          icon: MapIcon,
          groupIndex: 1,
          disabled: isPaymentLockEnabled,
          isAvailable: trip?.division !== Division.Superlite,
          id: "1-6",
          order: getOrder("1", "1-6") || 6,
        },
        {
          name: "Flight Details",
          path: "/experience/flight",
          icon: AirplaneIcon,
          groupIndex: 1,
          disabled: isPaymentLockEnabled,
          isAvailable:
            trip?.division !== Division.Superlite && !isWHArrivalOnly,
          id: "1-7",
          order: getOrder("1", "1-7") || 7,
        },
        // {
        //   name:
        //     trip?.division === Division.Superlite || isWHArrivalOnly
        //       ? "Local Jobs"
        //       : "Jobs",
        //   path: "/experience/jobs?tab=job-opportunities",
        //   groupIndex:
        //     trip?.division === Division.Superlite || isWHArrivalOnly ? 0 : 1,
        //   disabled: isPaymentLockEnabled,
        //   isAvailable:
        //     trip?.division === Division.WorkingHoliday ||
        //     trip?.division === Division.Superlite,
        //   id: "1-8",
        //   order: getOrder("1", "1-8") || 8,
        // },
        {
          name: "Arrival",
          path: "/experience/arrival",
          groupIndex: 1,
          icon: FlagIcon,
          disabled: isPaymentLockEnabled,
          isAvailable:
            trip?.division === Division.WorkingHoliday ||
            trip?.division === Division.SummerCamp,
          id: "1-9",
          order: getOrder("1", "1-9") || 9,
        },
        {
          name: "Accommodation",
          path: "/experience/accommodation",
          icon: AccommodationIcon,
          groupIndex: 1,
          disabled: isPaymentLockEnabled,
          isAvailable: trip?.division === Division.WorkingHoliday,
          id: "1-10",
          order: getOrder("1", "1-10") || 10,
        },
        {
          name: "Host Family",
          path: "/experience/host",
          groupIndex: 1,
          icon: HostFamilyIcon,
          disabled: isPaymentLockEnabled,
          isAvailable:
            trip?.division === Division.AuPair ||
            trip?.division === Division.Tutor,
          id: "1-11",
          order: getOrder("1", "1-11") || 11,
        },
        {
          name: "Tours",
          path: "/experience/tours",
          icon: TourIcon,
          groupIndex: 1,
          isAvailable: trip?.division !== Division.Getaways,
          disabled: isPaymentLockEnabled,

          id: "1-12",
          order: getOrder("1", "1-12") || 12,
        },
        {
          name: "Insurance",
          path: "/experience/insurance",
          icon: UmbrellaIcon,
          groupIndex: 1,
          disabled: isPaymentLockEnabled,
          id: "1-13",
          order: getOrder("1", "1-13") || 13,
        },
        {
          name: "Visas",
          path: "/experience/visas",
          icon: VisaIcon,
          groupIndex: 1,
          disabled: isPaymentLockEnabled,
          isAvailable:
            trip?.destination !== "Remote" && trip?.destination !== "Worldwide",
          id: "1-14",
          order: getOrder("1", "1-14") || 14,
        },
        {
          name: "Resources",
          path: "/experience/resources?tab=resources",
          icon: SourceIcon,
          groupIndex: 1,
          disabled: isPaymentLockEnabled,
          isAvailable:
            trip?.division === Division.Superlite ||
            trip?.division === Division.WorkingHoliday ||
            trip?.division === Division.Volunteer ||
            trip?.division === Division.Study ||
            trip?.division === Division.Teach ||
            trip?.division === Division.Getaways,
          id: "1-15",
          order: getOrder("1", "1-15") || 15,
        },
        {
          name: "Animal Welfare",
          path: "/experience/animal-welfare",
          icon: ConservationIcon,
          groupIndex: 1,
          disabled: isPaymentLockEnabled,
          id: "1-16",
          order: getOrder("1", "1-16") || 16,
        },
        {
          name: "Upgrade",
          path: "/experience/upgrade",
          icon: UpgradeIcon,
          groupIndex: 0,
          disabled: isPaymentLockEnabled,
          isAvailable: trip?.division === Division.Superlite || isWHArrivalOnly,
          id: "1-17",
          order: getOrder("1", "1-17") || 17,
        },
      ],
    },
    {
      name: isMobile ? "Deals" : "Marketplace",
      path: "/marketplace",
      icon: DealIcon,
      disabled: isTripCompleted,
      features: [RouteFeature.MobileTab, RouteFeature.Tab],
      id: "2",
      order: getOrder("2") || 2,
    },
    {
      name: isMobile ? "Learn" : "Academy",
      path: "/academy",
      icon: LearnIcon,
      disabled: isTripCompleted,
      features: [RouteFeature.MobileTab, RouteFeature.Tab],
      id: "3",
      order: getOrder("3") || 3,
      customMobileHeader: <AcademyMobileHeader />,
    },
    {
      name: "Social",
      // path: "/social/timeline?tab=feed",
      path: "/social/timeline",
      icon: SocialIcon,
      disabled: !hasSocialGroup,
      tooltipMessage:
        !hasSocialGroup && trip?.destination === "Worldwide"
          ? "Please contact us to activate Social."
          : !hasSocialGroup && !isTripCompleted
          ? `Your private social network, unlocked 30 days before your trip starts.`
          : "",
      features: [
        RouteFeature.MobileTab,
        RouteFeature.Tab,
        RouteFeature.Sidebar,
      ],
      customMobileHeader: <SocialMobileHeader />,
      customSidebar: <SocialSidebar />,
      rootClassName: "no-padding",
      isAvailable: trip?.destination !== "Remote",
      id: "4",
      order: getOrder("4") || 4,
      children: [
        {
          name: "Timeline",
          path: "/social/timeline?tab=feed",
          groupIndex: 0,
          id: "4-1",
          order: getOrder("4", "4-1") || 1,
        },
        {
          name: "Profile",
          path: "/social/profile",
          groupIndex: 0,
          id: "4-2",
          order: getOrder("4", "4-2") || 2,
        },
        {
          name: "Messages",
          path: "/social/messages",
          groupIndex: 0,
          features: [SubrouteFeature.HideChatPopover],
          id: "4-3",
          order: getOrder("4", "4-3") || 3,
        },
      ],
    },
    {
      name: "Notifications",
      path: "/notifications",
      icon: BellIcon,
      features: [RouteFeature.HasMobileTripSelector],
      id: "5",
      order: getOrder("5") || 5,
    },
    {
      name: "Jobs",
      path: "/jobs?tab=job-opportunities",
      icon: WorkIcon,
      features: [
        RouteFeature.MobileTab,
        RouteFeature.HasMobileTripSelector,
        RouteFeature.Tab,
      ],
      id: "12",
      order: getOrder("12") || 12,
      disabled: isPaymentLockEnabled,
      isAvailable:
        trip?.division === Division.WorkingHoliday ||
        trip?.division === Division.Superlite,
      customMobileHeader: <JobsMobileHeader />,
    },
    {
      name: "Account",
      path: "/account/general",
      features: [
        RouteFeature.Sidebar,
        RouteFeature.SidebarTitle,
        RouteFeature.HasMobileTripSelector,
      ],
      id: "6",
      order: getOrder("6") || 6,
      children: [
        {
          name: "General",
          path: "/account/general",
          groupIndex: 0,
          id: "6-1",
          order: getOrder("6", "6-1") || 1,
        },
        {
          name: "Contact",
          path: "/account/contact",
          groupIndex: 0,
          isAvailable:
            trip?.division !== Division.Superlite && !isWHArrivalOnly,
          id: "6-2",
          order: getOrder("6", "6-2") || 2,
        },
        {
          name: "Personal",
          path: "/account/personal",
          groupIndex: 0,
          isAvailable:
            trip?.division !== Division.Superlite && !isWHArrivalOnly,
          id: "6-3",
          order: getOrder("6", "6-3") || 3,
        },
        {
          name: "Visa",
          path: "/account/visa",
          groupIndex: 0,
          isAvailable: trip?.division === Division.WorkingHoliday,
          id: "6-4",
          order: getOrder("6", "6-4") || 4,
        },
        {
          name: "Preferences",
          path: "/account/preference",
          groupIndex: 0,
          isAvailable: trip?.division === Division.Getaways,
          id: "6-7",
          order: getOrder("6", "6-7") || 7,
        },
        {
          name: "Settings",
          path: "/account/settings",
          groupIndex: 1,
          id: "6-5",
          order: getOrder("6", "6-5") || 5,
        },
        {
          name: "About",
          path: "/account/about",
          groupIndex: 1,
          id: "6-6",
          order: getOrder("6", "6-6") || 6,
        },
      ],
    },
    {
      name: "Setup",
      path: "/setup",
      features: [RouteFeature.HideSuggestionBanner],
      id: "7",
      order: getOrder("7") || 7,
    },
    {
      name: "Finalise",
      path: "/finalise",
      features: [
        RouteFeature.HideSuggestionBanner,
        RouteFeature.HasMobileTripSelector,
      ],
      id: "8",
      order: getOrder("8") || 8,
    },
    {
      name: "Cancellation",
      path: "/cancellation",
      features: [RouteFeature.HasMobileTripSelector],
      id: "9",
      order: getOrder("9") || 9,
    },
    {
      name: "Lock",
      path: "/lock",
      features: [RouteFeature.HasMobileTripSelector],
      id: "10",
      order: getOrder("10") || 10,
    },
    {
      name: "Rewards",
      path: "/rewards?tab=reward-center",
      features: [RouteFeature.HasMobileTripSelector],
      id: "11",
      order: getOrder("11") || 11,
    },
  ];

  return routes
    .filter((route) => {
      if (route.isAvailable === undefined) return true;
      return route.isAvailable;
    })
    .map((route) => {
      if (route.children)
        route.children = route.children.filter((subroute) => {
          if (subroute.isAvailable === undefined) return true;

          return subroute.isAvailable;
        });

      return route;
    });
};

const useOrderedMenuByTripDivision = ({
  trip,
  hasSocialGroup,
  isPaymentLockEnabled,
  isTripCompleted,
  isAfricanRegion,
}: {
  trip: Trip | undefined | null;
  hasSocialGroup: boolean;
  isPaymentLockEnabled: boolean;
  isTripCompleted: boolean;
  isAfricanRegion: boolean;
}) => {
  const [sortedRoutes, setSortedRoutes] = useState<Array<Route>>([]);
  const { getOrder } = useDivisionMenuOrder(trip);
  const { isMobile } = useResponsive();

  const currentTrip: Trip | undefined | null = useMemo(() => trip, [trip?.id]);
  const currentTripCompletedStatus = useMemo(
    () => isTripCompleted,
    [isTripCompleted]
  );
  const currentSocialStatus = useMemo(() => hasSocialGroup, [hasSocialGroup]);
  const currentPaymentLock = useMemo(
    () => isPaymentLockEnabled,
    [isPaymentLockEnabled]
  );
  const currentAfricanRegionStatus = useMemo(
    () => isAfricanRegion,
    [isAfricanRegion]
  );
  const mobileStatus = useMemo(() => isMobile, [isMobile]);

  const routes: Array<Route> = useMemo(
    () =>
      getRoutes({
        trip: currentTrip,
        hasSocialGroup: currentSocialStatus,
        isPaymentLockEnabled: currentPaymentLock,
        isTripCompleted: currentTripCompletedStatus,
        isAfricanRegion: currentAfricanRegionStatus,
        getOrder,
        isMobile: mobileStatus,
      }),
    [
      currentTrip,
      currentTripCompletedStatus,
      currentSocialStatus,
      currentPaymentLock,
      currentAfricanRegionStatus,
      mobileStatus,
      getOrder,
    ]
  );

  useEffect(() => {
    const currentSortedRoutes = routes
      .map((eachRoute) => {
        if (eachRoute.children) {
          const sortedChildren = [...eachRoute.children].sort(
            (firstChild, secondChild) =>
              (firstChild.order || 1) - (secondChild.order || 0)
          );
          return { ...eachRoute, children: sortedChildren };
        }
        return eachRoute;
      })
      .sort(
        (firstRoute, secondRoute) =>
          (firstRoute.order || 1) - (secondRoute.order || 0)
      );
    setSortedRoutes(currentSortedRoutes);
  }, [
    currentTrip,
    currentSocialStatus,
    currentPaymentLock,
    currentTripCompletedStatus,
    currentAfricanRegionStatus,
    routes,
  ]);

  return sortedRoutes;
};

export default useOrderedMenuByTripDivision;
